import { useEffect, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core';
import {
  getMaterialClassCodes,
  getUom,
  setNewCodeTranslation,
  setMaterialClassCodes,
  setNewUom,
} from '../../Actions';

// Which dataPreview (target) columns carry the value for each code field.
export const SKIP_SOURCE_FIELDS = {
  UOM: ['uom_type', 'selling_unit_uom'],
  MATERIAL_CLASS_CD: ['material_class'],
  BASIS_CD: ['basis_cd'],
};

// Drop rows from a preview whose value (in the relevant column) matches a
// skipped (fieldName, value) pair, and clear those issues. Returns a new
// preview plus how many rows were removed. Used for the in-memory (JSON) path.
export function applySkips(preview, skipSpecs) {
  if (!skipSpecs?.length || !preview?.dataPreview?.length) return preview;
  const header = preview.dataPreview[0];
  const rows = preview.dataPreview.slice(1);
  const specs = skipSpecs.map((s) => ({
    cols: (SKIP_SOURCE_FIELDS[s.fieldName] || []).map((f) => header.indexOf(f)).filter((i) => i >= 0),
    value: s.value,
  }));
  const keptRows = rows.filter((row) => !specs.some((sp) => sp.cols.some((c) => row[c] === sp.value)));
  const remainingIssues = (preview.issues ?? []).filter(
    (it) => !skipSpecs.some((s) => s.fieldName === it.fieldName && s.value === it.value)
  );
  return { ...preview, dataPreview: [header, ...keptRows], issues: remainingIssues, skippedCount: rows.length - keptRows.length };
}

const issueKey = (it) => `${it.fieldName}::${it.value}`;

// Dialog that lets an admin resolve unrecognized material class / UOM / basis
// values by mapping to an existing code, creating a new one, or skipping the
// affected rows. Map/create choices are persisted to code_translation; skip
// choices are returned to the host (which handles them per import type).
export default function ResolveCodesDialog({ open, issues, onCancel, onApply, notify }) {
  const [resolutions, setResolutions] = useState({});
  const [busy, setBusy] = useState(false);
  const [validMaterialClasses, setValidMaterialClasses] = useState([]);
  const [validUoms, setValidUoms] = useState([]);

  useEffect(() => {
    if (!open) return undefined;
    setResolutions({});
    let active = true;
    (async () => {
      const [mc, uom] = await Promise.all([
        getMaterialClassCodes().catch(() => null),
        getUom().catch(() => null),
      ]);
      if (!active) return;
      setValidMaterialClasses(mc?.data?.data ?? []);
      setValidUoms(uom?.data?.data ?? []);
    })();
    return () => { active = false; };
  }, [open]);

  function setResolution(key, patch) {
    setResolutions((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  }

  function optionsForIssue(it) {
    if (it.fieldName === 'MATERIAL_CLASS_CD') {
      return (Array.isArray(validMaterialClasses) ? validMaterialClasses : [])
        .map((m) => ({ code: m.material_class_cd, label: m.material_class }));
    }
    if (it.fieldName === 'UOM') {
      return (Array.isArray(validUoms) ? validUoms : [])
        .map((u) => ({ code: u.uom_type_cd, label: u.uom_type }));
    }
    return [];
  }

  function isComplete() {
    return (issues ?? []).every((it) => {
      const r = resolutions[issueKey(it)];
      if (!r || !r.mode) return false;
      if (r.mode === 'skip') return true;
      if (r.mode === 'map') return !!r.target;
      if (r.mode === 'create') return !!(r.newCode && r.newLabel);
      return false;
    });
  }

  async function handleApply() {
    setBusy(true);
    const skipSpecs = [];
    for (const it of issues) {
      const r = resolutions[issueKey(it)];
      if (r.mode === 'skip') {
        skipSpecs.push({ fieldName: it.fieldName, field: it.field, value: it.value });
        continue;
      }
      try {
        if (r.mode === 'map') {
          await setNewCodeTranslation(it.fieldName, it.value, r.target);
        } else if (it.fieldName === 'MATERIAL_CLASS_CD') {
          await setMaterialClassCodes(r.newCode, r.newLabel);
          if (r.newCode !== it.value) {
            await setNewCodeTranslation('MATERIAL_CLASS_CD', it.value, r.newCode);
          }
        } else if (it.fieldName === 'UOM') {
          await setNewUom(r.newLabel, r.newCode);
          if (r.newCode !== it.value) {
            await setNewCodeTranslation('UOM', it.value, r.newCode);
          }
        } else {
          await setNewCodeTranslation(it.fieldName, it.value, r.newCode);
        }
      } catch (e) {
        setBusy(false);
        notify?.('error', `Failed to save mapping for "${it.value}". Please try again.`);
        return;
      }
    }
    setBusy(false);
    await onApply(skipSpecs);
  }

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="md">
      <DialogTitle>Resolve Unknown Codes</DialogTitle>
      <DialogContent>
        <DialogContentText>
          This file contains material class / UOM / basis values that aren't recognized yet. Map each to an
          existing code, create a new one, or skip the affected rows. Your map/create choices are saved, so
          future files recognize them automatically.
        </DialogContentText>
        {(issues ?? []).map((it) => {
          const key = issueKey(it);
          const r = resolutions[key] || {};
          const opts = optionsForIssue(it);
          return (
            <Grid container spacing={1} key={key} alignItems="center" style={{borderBottom: '1px solid #eee', padding: '8px 0'}}>
              <Grid item xs={12}>
                <Typography variant="body2">
                  <strong>{it.label}:</strong> "{it.value || '(empty)'}"{' '}
                  <span style={{color: '#888'}}>({it.count} row{it.count === 1 ? '' : 's'})</span>
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  variant="outlined"
                  label="Resolution"
                  value={r.mode === 'create' ? '__create__' : r.mode === 'skip' ? '__skip__' : (r.target ?? '')}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '__create__') {
                      const codeLen = it.fieldName === 'MATERIAL_CLASS_CD' ? 3 : 10;
                      setResolution(key, { mode: 'create', target: undefined, newCode: (it.value || '').slice(0, codeLen).toUpperCase(), newLabel: it.value });
                    } else if (v === '__skip__') {
                      setResolution(key, { mode: 'skip', target: undefined, newCode: undefined, newLabel: undefined });
                    } else {
                      setResolution(key, { mode: 'map', target: v, newCode: undefined, newLabel: undefined });
                    }
                  }}
                >
                  {opts.map((o) => (
                    <MenuItem key={o.code} value={o.code}>
                      Map to {o.code}{o.label && o.label !== o.code ? ` (${o.label})` : ''}
                    </MenuItem>
                  ))}
                  <MenuItem value="__create__">➕ Create new code…</MenuItem>
                  <MenuItem value="__skip__">⊘ Skip rows with this value</MenuItem>
                </TextField>
              </Grid>
              {r.mode === 'skip' && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" style={{color: '#b26a00'}}>
                    {it.count} row{it.count === 1 ? '' : 's'} will be excluded from the import.
                  </Typography>
                </Grid>
              )}
              {r.mode === 'create' && (
                <>
                  <Grid item xs={6} sm={3}>
                    <TextField fullWidth size="small" variant="outlined" label="New code" value={r.newCode ?? ''} onChange={(e) => setResolution(key, { newCode: e.target.value.toUpperCase() })} />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField fullWidth size="small" variant="outlined" label="Label" value={r.newLabel ?? ''} onChange={(e) => setResolution(key, { newLabel: e.target.value })} />
                  </Grid>
                </>
              )}
            </Grid>
          );
        })}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" style={{margin: "10px"}} onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!isComplete() || busy}
          style={{backgroundColor: "#142e3e", color: "#28c4fc", margin: "10px"}}
          onClick={handleApply}
        >
          {busy ? <CircularProgress size={16} style={{color: "#28c4fc"}} /> : 'Save & Continue'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
